/**
 * Hali Leads Agent - White-Label Invoice Factoring Education & Qualification Assistant
 * 
 * This component provides an AI chatbot interface for invoice factoring companies
 * to educate customers and qualify leads. It's designed to be white-labeled and
 * dynamically configured per client tenant.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Send, MessageCircle, Minimize2, Bot, User, Loader, Sparkles, Paperclip, Users, Trash2 } from 'lucide-react';
import { useDarkMode } from '../../DarkModeContext';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import api from '../../lib/api';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './HaliLeadsAgent.css';

const MySwal = withReactContent(Swal);

const HaliLeadsAgent = ({
  initiallyOpen = false,
  onOpenChange,
  position = 'bottom-right', // 'bottom-right', 'bottom-left', 'top-right', 'top-left'
  compact = false, // Compact mode for smaller screens
  isLiveMode = false, // Live mode hides internal features like qualification badge
  demoMode = false,
  user = null, // User object for auto-populating contact form
  embedded = false // Render inside another container (no floating launcher)
}) => {
  const [isOpen, setIsOpen] = useState(embedded ? true : initiallyOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isAIOnline, setIsAIOnline] = useState(true);
  const [dragState, setDragState] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });
  const [customPosition, setCustomPosition] = useState(null);
  const [qualificationStatus, setQualificationStatus] = useState(null); // 'qualified', 'borderline', 'disqualified', null
  const [currentQuickReplies, setCurrentQuickReplies] = useState(null); // Dynamic quick replies from AI
  const [contactDraft, setContactDraft] = useState({
    fullName: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    email: user?.email || '',
    company: user?.company_name || '',
    phone: user?.phone || ''
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [facilityUi, setFacilityUi] = useState(null);
  const [facilityDraft, setFacilityDraft] = useState({
    amount: '',
    currency: 'USD',
    currencies: ['USD', 'EUR']
  });
  const [autoLogin, setAutoLogin] = useState(null);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [lastUploadedDocId, setLastUploadedDocId] = useState(null);
  const [lastLeadId, setLastLeadId] = useState(null);

  // Safe access to contexts with fallbacks
  const darkModeContext = useDarkMode();
  const whiteLabelContext = useWhiteLabel();
  const darkMode = darkModeContext?.darkMode || false;
  const branding = whiteLabelContext?.branding || {};

  const userKey = useMemo(() => {
    return (
      user?.id ||
      user?.user_id ||
      user?.email ||
      'guest'
    );
  }, [user?.id, user?.user_id, user?.email]);
  const conversationStorageKey = useMemo(() => {
    const tenantKey = branding?.id ? String(branding.id) : 'default';
    return `haliLeadsConversationId:${tenantKey}:${userKey}`;
  }, [branding?.id, userKey]);
  const conversationFallbackKey = useMemo(() => {
    return `haliLeadsConversationId:last:${userKey}`;
  }, [userKey]);
  const lastUserKeyRef = useRef(userKey);

  const seedWelcomeMessage = () => {
    const companyName = branding?.companyName || 'our company';
    const welcomeMessage = `Hi! I'm ${companyName}'s sales consultant, here to help you understand if invoice factoring is right for your business.\n\nWhat brings you here today? If you have an invoice handy, you can upload it for a quick eligibility check.\n\n- 💰 I need faster cash flow\n- ✅ Start exploring your qualification\n- 📚 I'm just learning about factoring\n- 📄 I want to upload an invoice for a quick check\n- 🚀 I'm ready to apply`;

    setMessages([{
      type: 'bot',
      text: welcomeMessage,
      timestamp: new Date()
    }]);

    setCurrentQuickReplies([
      { label: '💰 I need faster cash flow', value: 'I need faster cash flow' },
      { label: '✅ Start exploring your qualification', value: 'Start exploring your qualification' },
      { label: '📚 I\'m just learning about factoring', value: 'I\'m just learning about factoring' },
      { label: '📄 Upload an invoice for a quick check', value: 'I want to upload an invoice for a quick check' },
      { label: '🚀 I\'m ready to apply', value: 'I\'m ready to apply' }
    ]);
  };

  // Auto-populate contact form when user is available
  useEffect(() => {
    console.log('[HaliLeadsAgent] User prop received:', user);
    if (user) {
      console.log('[HaliLeadsAgent] Auto-populating contact form with:', {
        fullName: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        email: user.email || '',
        company: user.company_name || '',
        phone: user.phone || ''
      });
      setContactDraft({
        fullName: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        email: user.email || '',
        company: user.company_name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // Initialize with welcome message (sales-focused)
  useEffect(() => {
    if (messages.length === 0) {
      seedWelcomeMessage();
    }
  }, [branding?.companyName]);

  // Clear known-bad cached API base values without forcing reloads.
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem('apiBase');
      if (cached && /localhost:5002/.test(cached)) {
        console.warn('Clearing stale apiBase from session storage:', cached);
        sessionStorage.removeItem('apiBase');
      }
    } catch (e) {
      // Ignore
    }
  }, []);

  useEffect(() => {
    if (!conversationId) return;
    try {
      localStorage.setItem(conversationStorageKey, conversationId);
      localStorage.setItem(conversationFallbackKey, conversationId);
    } catch (_) {}
  }, [conversationId, conversationStorageKey, conversationFallbackKey]);

  useEffect(() => {
    if (lastUserKeyRef.current === userKey) return;
    // User changed: prevent conversation leakage across accounts.
    try {
      if (conversationStorageKey) localStorage.removeItem(conversationStorageKey);
      if (conversationFallbackKey) localStorage.removeItem(conversationFallbackKey);
    } catch (_) {}
    lastUserKeyRef.current = userKey;
    setConversationId(null);
    setMessages([]);
    seedWelcomeMessage();
  }, [userKey, conversationStorageKey, conversationFallbackKey]);

  useEffect(() => {
    if (!conversationStorageKey || conversationId) return;
    let stored = null;
    try {
      stored = localStorage.getItem(conversationStorageKey);
      if (!stored && userKey === 'guest') {
        stored = localStorage.getItem(conversationFallbackKey);
      }
    } catch (_) {
      stored = null;
    }
    if (!stored) return;

    api.get(`/api/hali-leads-agent/conversation/${stored}`)
      .then((response) => {
        if (!response?.data?.success) return;
        const dbMessages = response.data.conversation?.messages || [];
        if (!Array.isArray(dbMessages) || dbMessages.length === 0) return;
        const mapped = dbMessages.map((msg) => ({
          type: msg.role === 'assistant' ? 'bot' : 'user',
          text: msg.content || msg.text || '',
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
        }));
        setConversationId(stored);
        try {
          localStorage.setItem(conversationStorageKey, stored);
        } catch (_) {}
        setMessages(mapped);
      })
      .catch(() => {
        // If the cached conversation no longer exists (DB reset, tenant changed, etc.),
        // clear it so we don't keep retrying on every mount.
        try {
          localStorage.removeItem(conversationStorageKey);
          localStorage.removeItem(conversationFallbackKey);
        } catch (_) {}
      });
  }, [conversationId, conversationStorageKey, conversationFallbackKey, userKey]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderBotMessageContent = (text) => {
    const lines = String(text || '').split('\n');
    return lines.map((line, index) => {
      const match = line.match(/^-\s*([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}])\s+(.+)$/u);
      if (!match) {
        return (
          <span key={`${index}-line`}>
            {line}
            {index < lines.length - 1 ? '\n' : ''}
          </span>
        );
      }

      const label = `${match[1]} ${match[2]}`.trim();
      const value = match[2].trim();
      return (
        <span key={`${index}-option`} className="block">
          <button
            type="button"
            onClick={() => handleQuickReply(value)}
            className="text-xs px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-colors text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 mt-2"
            style={{
              borderColor: branding?.primaryColor ? `${branding.primaryColor}40` : undefined
            }}
          >
            {label}
          </button>
        </span>
      );
    });
  };

  const isConfirmationPrompt = (text) => {
    const normalized = String(text || '').toLowerCase();
    return normalized.includes('please confirm these details');
  };

  const isContactPrompt = (text) => {
    const normalized = String(text || '').toLowerCase();
    return normalized.includes('please share') &&
      normalized.includes('full name') &&
      normalized.includes('email') &&
      normalized.includes('company') &&
      normalized.includes('mobile');
  };

  const handleContactSubmit = async () => {
    if (contactSubmitting) return;
    const payloadText = `Name: ${contactDraft.fullName}\nEmail: ${contactDraft.email}\nCompany: ${contactDraft.company}\nMobile: ${contactDraft.phone}`;
    setContactSubmitting(true);
    try {
      await handleSendMessage(payloadText);
      setContactDraft({ fullName: '', email: '', company: '', phone: '' });
    } finally {
      setContactSubmitting(false);
    }
  };

  // Format money value
  const formatMoney = (val) => {
    const n = Number(String(val || '').replace(/[^0-9.\-]/g, '')) || 0;
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Show popup when lead is created
  const showLeadCreatedPopup = async (leadId, createdRecords = {}) => {
    try {
      // Fetch lead details
      const response = await api.get(`/api/crm-leads/${leadId}`);
      const lead = response.data?.data || response.data?.lead || response.data;
      
      if (!lead) {
        console.warn('Lead details not found for popup');
        return;
      }

      // Extract details from lead
      const leadNumber = lead.lead_number || lead.id;
      const companyName = lead.company || lead.company_name || 'N/A';
      const contactName = lead.first_name && lead.last_name 
        ? `${lead.first_name} ${lead.last_name}`.trim()
        : lead.email || 'N/A';
      const contactEmail = lead.email || 'N/A';
      const contactPhone = lead.phone || lead.mobile || 'N/A';
      const qualificationStatus = lead.qualification_status || lead.status || 'pending';
      const facilityAmount = lead.facility_amount || lead.monthly_invoice_volume || null;
      const currency = lead.currency || 'USD';

      // Status color mapping
      const statusColors = {
        qualified: darkMode ? '#34d399' : '#047857',
        borderline: darkMode ? '#fbbf24' : '#d97706',
        disqualified: darkMode ? '#f87171' : '#dc2626',
        pending: darkMode ? '#fbbf24' : '#d97706'
      };
      const statusColor = statusColors[qualificationStatus] || statusColors.pending;

      // Show success popup
      await MySwal.fire({
        icon: 'success',
        title: 'Lead Created Successfully!',
        html: `
          <div style="text-align: left; padding: 10px;">
            <p style="margin-bottom: 15px; font-weight: 500;">Your lead has been created and the following records have been set up:</p>

            <div style="background: ${darkMode ? '#065f46' : '#d1fae5'}; border-radius: 8px; padding: 12px; margin-bottom: 15px; border: 2px solid ${darkMode ? '#10b981' : '#059669'};">
              <p style="margin: 0 0 8px 0; font-weight: 600; color: ${darkMode ? '#34d399' : '#047857'};">✓ Lead Created</p>
              <ul style="margin: 0 0 0 20px; padding: 0; font-size: 13px; list-style: disc;">
                ${leadNumber ? `<li>Lead ID: <strong>HT-${String(leadNumber).padStart(5, '0')}</strong></li>` : ''}
                <li>Company: <strong>${companyName}</strong></li>
                <li>Contact: <strong>${contactName}</strong></li>
                <li>Email: <strong>${contactEmail}</strong></li>
                ${contactPhone !== 'N/A' ? `<li>Phone: <strong>${contactPhone}</strong></li>` : ''}
                ${facilityAmount ? `<li>Facility Amount: <strong>${currency} ${formatMoney(facilityAmount)}</strong></li>` : ''}
                <li>Status: <strong style="color: ${statusColor};">${qualificationStatus.charAt(0).toUpperCase() + qualificationStatus.slice(1)}</strong></li>
              </ul>
            </div>

            ${createdRecords.opportunityId ? `
            <div style="background: ${darkMode ? '#111827' : '#eef2ff'}; border-radius: 8px; padding: 12px; margin-bottom: 15px; border: 2px solid ${darkMode ? '#6366f1' : '#4f46e5'};">
              <p style="margin: 0 0 8px 0; font-weight: 600; color: ${darkMode ? '#c7d2fe' : '#312e81'};">✓ Opportunity Created</p>
              <ul style="margin: 0 0 0 20px; padding: 0; font-size: 13px; list-style: disc;">
                <li>ID: <strong>${createdRecords.opportunityId}</strong></li>
                ${facilityAmount ? `<li>Facility Amount: <strong>${currency} ${formatMoney(facilityAmount)}</strong></li>` : ''}
                <li>Status: <strong>Pending Review</strong></li>
              </ul>
            </div>
            ` : ''}

            ${createdRecords.validationQueueId ? `
            <div style="background: ${darkMode ? '#0f172a' : '#f8fafc'}; border-radius: 8px; padding: 12px; margin-bottom: 15px; border: 2px solid ${darkMode ? '#334155' : '#cbd5e1'};">
              <p style="margin: 0 0 8px 0; font-weight: 600; color: ${darkMode ? '#e5e7eb' : '#0f172a'};">✓ Validation Queue</p>
              <ul style="margin: 0 0 0 20px; padding: 0; font-size: 13px; list-style: disc;">
                <li>ID: <strong>${createdRecords.validationQueueId}</strong></li>
                <li>Status: <strong>Pending</strong></li>
              </ul>
            </div>
            ` : ''}

            <div style="background: ${darkMode ? '#374151' : '#f3f4f6'}; border-radius: 8px; padding: 12px; margin-bottom: 15px;">
              <p style="margin: 0 0 8px 0; font-weight: 600; color: ${darkMode ? '#60a5fa' : '#2563eb'};">✓ Next Steps</p>
              <ul style="margin: 0 0 0 20px; padding: 0; font-size: 13px; list-style: disc;">
                <li>Our team will review your submission</li>
                <li>You'll receive an email confirmation shortly</li>
                <li>We'll contact you to discuss next steps</li>
              </ul>
            </div>
          </div>
        `,
        confirmButtonText: 'Continue',
        confirmButtonColor: branding?.primaryColor || '#3085d6',
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827',
        width: '600px'
      });
    } catch (error) {
      console.error('Error fetching lead details for popup:', error);
      // Show a simpler popup if we can't fetch details
      await MySwal.fire({
        icon: 'success',
        title: 'Lead Created!',
        text: `Your lead has been created successfully. Reference ID: ${leadId}`,
        confirmButtonText: 'OK',
        confirmButtonColor: branding?.primaryColor || '#3085d6',
        background: darkMode ? '#1F2937' : '#fff',
        color: darkMode ? '#F9FAFB' : '#111827'
      });
    }
  };

  // Format number with thousands separators
  const formatNumberWithSeparator = (value) => {
    if (!value) return '';
    
    // Remove all non-digit characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    if (!numericValue) return '';
    
    // Handle multiple decimal points - only keep first one
    const firstDecimalIndex = numericValue.indexOf('.');
    let cleanValue = numericValue;
    if (firstDecimalIndex !== -1) {
      // Keep everything before first decimal and everything after (remove extra decimals)
      const beforeDecimal = numericValue.substring(0, firstDecimalIndex);
      const afterDecimal = numericValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
      cleanValue = afterDecimal ? `${beforeDecimal}.${afterDecimal}` : beforeDecimal;
    }
    
    const parts = cleanValue.split('.');
    const integerPart = parts[0] || '';
    const decimalPart = parts[1];
    
    // Format integer part with commas
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Combine with decimal part if exists
    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  };

  // Remove formatting to get raw numeric value
  const parseFormattedNumber = (value) => {
    return value.replace(/,/g, '');
  };

  const handleFacilitySubmit = async () => {
    if (isTyping || !facilityDraft.amount) return;
    // Remove commas before sending
    const rawAmount = parseFormattedNumber(facilityDraft.amount);
    const payloadText = `Facility Amount: ${rawAmount} ${facilityDraft.currency}`;
    setFacilityUi(null);
    setFacilityDraft((prev) => ({ ...prev, amount: '' }));
    await handleSendMessage(payloadText);
  };

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
    }
  }, [messages, isOpen, isMinimized]);

  useEffect(() => {
    if (!dragState.isDragging) return;

    const handleMouseMove = (event) => {
      if (!containerRef.current) return;
      const width = containerRef.current.offsetWidth;
      const height = containerRef.current.offsetHeight;
      const nextX = event.clientX - dragState.offsetX;
      const nextY = event.clientY - dragState.offsetY;
      const maxX = Math.max(8, window.innerWidth - width - 8);
      const maxY = Math.max(8, window.innerHeight - height - 8);

      setCustomPosition({
        x: Math.min(Math.max(8, nextX), maxX),
        y: Math.min(Math.max(8, nextY), maxY)
      });
    };

    const handleMouseUp = () => {
      setDragState((prev) => ({ ...prev, isDragging: false }));
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState.isDragging, dragState.offsetX, dragState.offsetY]);

  // Only sync on mount, not on every initiallyOpen change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!embedded) {
      setIsOpen(initiallyOpen);
    }
  }, []);

  // Default quick reply suggestions for common questions (shown when no AI suggestions)
  const defaultQuickReplies = [
    { label: '💰 I need faster cash flow', value: 'I need faster cash flow' },
    { label: '✅ Do I qualify?', value: 'Do I qualify for invoice factoring?' },
    { label: '📚 Learn about factoring', value: 'What is invoice factoring?' }
  ];

  // Send message to Hali Leads Agent backend
  const sendMessageToAI = async (text, explicitDocId = null) => {
    try {
      // Prepare payload with optional document ID if just uploaded
      const payload = {
        message: text,
        conversationId: conversationId,
        tenantId: branding?.id || null,
        demoMode,
        context: {
          companyName: branding?.companyName || null,
          companyUrl: branding?.websiteUrl || null,
          supportEmail: branding?.supportEmail || null,
          supportPhone: branding?.supportPhone || null
        }
      };

      // Attach document ID if we have one pending analysis (explicit or from state)
      const docIdToSend = explicitDocId || lastUploadedDocId;
      if (docIdToSend) {
        payload.documentId = docIdToSend;
        // Clear it so we don't send it again
        setLastUploadedDocId(null);
      }

      const response = await api.post('/api/hali-leads-agent/chat', payload);

      if (response.data.success) {
        setIsAIOnline(true);

        // Save conversation ID for context
        if (response.data.conversationId && !conversationId) {
          setConversationId(response.data.conversationId);
        }

        // Check for qualification status in response
        if (response.data.qualificationStatus) {
          setQualificationStatus(response.data.qualificationStatus);
        }

        // Set quick replies from AI response if available
        if (response.data.quickReplies && Array.isArray(response.data.quickReplies)) {
          setCurrentQuickReplies(response.data.quickReplies);
        } else {
          setCurrentQuickReplies(null);
        }

        if (response.data.ui?.type === 'facility_amount') {
          const uiConfig = response.data.ui;
          setFacilityUi(uiConfig);
          setFacilityDraft((prev) => ({
            amount: '',
            currency: uiConfig.currency || prev.currency,
            currencies: Array.isArray(uiConfig.currencies) ? uiConfig.currencies : prev.currencies
          }));
        } else {
          setFacilityUi(null);
        }

        return {
          message: response.data.message,
          leadId: response.data.leadId || null,
          autoLogin: response.data.autoLogin || null
        };
      } else {
        throw new Error('Failed to get response from AI');
      }
    } catch (error) {
      console.error('Hali Leads Agent Error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code,
        hasResponse: Boolean(error?.response),
        stack: error.stack
      });
      // Log full error response for debugging
      if (error.response?.data) {
        console.error('Full error response:', JSON.stringify(error.response.data, null, 2));
      }
      
      const hasResponse = Boolean(error?.response);
      if (!hasResponse) {
        setIsAIOnline(false);
      }

      // Rate limiting
      if (error.response?.status === 429) {
        return {
          message: "I'm experiencing high demand right now. Please try again in a moment, or contact us directly.",
          leadId: null
        };
      }

      // Network errors (no connection)
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        const supportContact = branding?.supportEmail
          ? `\n\n📧 Email: ${branding.supportEmail}${branding?.supportPhone ? `\n📞 Phone: ${branding.supportPhone}` : ''}`
          : '\n\n📧 Email: support@halisoft.biz';
        
        return {
          message: `I'm having trouble connecting to the server right now. This might be a temporary network issue. Please try again in a moment, or contact our team directly:${supportContact}`,
          leadId: null
        };
      }

      // Backend errors (500, 400, etc.) - use backend's error message if available
      if (error.response?.data?.message) {
        return {
          message: error.response.data.message,
          leadId: null
        };
      }

      // Fallback for other errors
      const supportContact = branding?.supportEmail
        ? `\n\n📧 Email: ${branding.supportEmail}${branding?.supportPhone ? `\n📞 Phone: ${branding.supportPhone}` : ''}`
        : '\n\n📧 Email: support@halisoft.biz';

      return {
        message: `I'm having trouble processing your request. Please try again in a moment, or contact our team directly:${supportContact}`,
        leadId: null
      };
    }
  };

  const handleSendMessage = async (text = inputValue, explicitDocId = null) => {
    if (!text.trim() || isTyping) return;

    const userText = text.trim();

    // Add user message
    const userMessage = {
      type: 'user',
      text: userText,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      // Get AI response
      const aiResponse = await sendMessageToAI(userText, explicitDocId);

      setIsTyping(false);

      // Add bot message
      const botMessage = {
        type: 'bot',
        text: aiResponse.message,
        timestamp: new Date()
      };

      // Show success popup when lead is created
      if (aiResponse.leadId && aiResponse.leadId !== lastLeadId) {
        setLastLeadId(aiResponse.leadId);
        // Fetch lead details and show popup
        showLeadCreatedPopup(aiResponse.leadId, aiResponse.createdRecords || {}).catch(err => {
          console.error('Error showing lead created popup:', err);
        });
      }

      setMessages(prev => [
        ...prev,
        botMessage
      ]);

      if (aiResponse.autoLogin?.token) {
        try {
          localStorage.setItem('token', aiResponse.autoLogin.token);
          localStorage.setItem('lead-invite-activated', String(Date.now()));
          localStorage.setItem('selectedWhiteLabelProfile', 'halitrade');
          sessionStorage.setItem('multiTenant.currentTenant', 'halitrade');
          sessionStorage.setItem('just_logged_in', '1');
          sessionStorage.setItem('just_navigated', '1');
          localStorage.removeItem('demo-auth-token');
          localStorage.removeItem('demo-auth-users');
          localStorage.removeItem('demo-auth-current-user');
          localStorage.setItem('haliSalesContinue', '1');
        } catch (_) {}
        const safeUrl = (() => {
          if (!aiResponse.autoLogin?.redirectUrl) return null;
          if (!conversationId) return aiResponse.autoLogin.redirectUrl;
          try {
            const url = new URL(aiResponse.autoLogin.redirectUrl, window.location.origin);
            if (!url.searchParams.get('conversationId')) {
              url.searchParams.set('conversationId', conversationId);
            }
            return url.toString();
          } catch (_) {
            return aiResponse.autoLogin.redirectUrl;
          }
        })();
        setAutoLogin({
          ...aiResponse.autoLogin,
          redirectUrl: safeUrl || aiResponse.autoLogin.redirectUrl
        });
      }
    } catch (error) {
      setIsTyping(false);
      console.error('Error sending message:', error);

      // This catch should rarely be hit since sendMessageToAI handles errors internally
      // But if it does, use the error message from the backend if available
      const errorText = error.response?.data?.message 
        || error.message 
        || "I'm having trouble processing your request. Please try again in a moment, or contact our team directly.";

      const supportContact = branding?.supportEmail
        ? `\n\n📧 Email: ${branding.supportEmail}${branding?.supportPhone ? `\n📞 Phone: ${branding.supportPhone}` : ''}`
        : '\n\n📧 Email: support@halisoft.biz';

      const errorMessage = {
        type: 'bot',
        text: errorText + supportContact,
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);

    try {
      // Convert to Base64
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const base64Data = reader.result;

        // Upload to API
        const response = await api.post('/api/hali-leads-agent/upload', {
          file: base64Data,
          filename: file.name,
          mimeType: file.type,
          conversationId: conversationId,
          tenantId: branding?.id
        });

        if (response.data.success) {
          const newDocId = response.data.documentId;
          setLastUploadedDocId(newDocId);

          // Add user message indicating upload
          const userMessage = {
            type: 'user',
            text: `📄 Uploaded document: ${file.name}`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, userMessage]);

          // Trigger AI response acknowledging receipt
          // We'll simulate receiving it by sending a natural prompt
          // This allows the user to see the "Uploaded" message, then the "Sending..." status

          // Wait briefly for UI to update
          setTimeout(() => {
            // We initiate the message but DON'T add a user bubble for this prompt (it's implicit)
            // Actually, sticking to the existing pattern: call handleSendMessage with text
            // But we want to ensure the Doc ID is passed.

            // We can just call handleSendMessage() with a prompt.
            handleSendMessage(`I've uploaded ${file.name}. Can you analyze this invoice and tell me if it meets your eligibility criteria?`, newDocId);
          }, 500);
        }
        setIsUploading(false);
      };
      reader.onerror = () => {
        console.error('File reading failed');
        setIsUploading(false);
      };
    } catch (error) {
      console.error('Upload failed:', error);
      setIsUploading(false);
      alert('Failed to upload file. Please try again.');
    }

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleQuickReply = (value) => {
    const normalized = String(value || '').toLowerCase();
    const wantsUpload = normalized.includes('upload an invoice') || normalized.includes('quick check');

    if (wantsUpload) {
      setMessages((prev) => [
        ...prev,
        {
          type: 'bot',
          text: 'Great — to run a quick check, please upload your invoice.\n\n1) Click the paperclip (or “Choose File”) below\n2) Select your invoice PDF or image\n3) I’ll analyze it and summarize the key details\n\nIf you have multiple invoices, start with one.',
          timestamp: new Date()
        }
      ]);
      setTimeout(() => {
        fileInputRef.current?.click();
      }, 200);
      return;
    }

    handleSendMessage(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    if (embedded) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus input when opening
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const toggleMinimize = () => {
    if (embedded) return;
    setIsMinimized(!isMinimized);
  };

  const handleResetConversation = async () => {
    const resetState = () => {
      setMessages([]);
      seedWelcomeMessage();
      setConversationId(null);
      setQualificationStatus(null);
      setCurrentQuickReplies(null);
      setFacilityUi(null);
      setFacilityDraft({ amount: '', currency: 'USD', currencies: ['USD', 'EUR'] });
      setAutoLogin(null);
      setLastLeadId(null);
    };

    if (!conversationId) {
      resetState();
      return;
    }

    try {
      await api.delete(`/api/hali-leads-agent/conversation/${conversationId}`);
    } catch (_) {
      // Ignore reset errors
    }

    try {
      localStorage.removeItem(conversationStorageKey);
    } catch (_) {}

    resetState();
  };

  // Get position classes
  const getPositionClasses = () => {
    const positions = {
      'bottom-right': 'bottom-20 right-8',
      'bottom-left': 'bottom-20 left-8',
      'top-right': 'top-20 right-8',
      'top-left': 'top-20 left-8'
    };
    return positions[position] || positions['bottom-right'];
  };

  // Qualification status badge color
  const getQualificationBadgeColor = () => {
    switch (qualificationStatus) {
      case 'qualified':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'borderline':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'disqualified':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getQualificationLabel = () => {
    switch (qualificationStatus) {
      case 'qualified':
        return '✓ Qualified';
      case 'borderline':
        return '⚠️ Borderline';
      case 'disqualified':
        return '✗ Not Qualified';
      default:
        return null;
    }
  };

  if (!isOpen && !embedded) {
    return (
      <button
        onClick={toggleChat}
        className={`fixed ${getPositionClasses()} z-50 p-2 md:p-2 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 group backdrop-blur-xl border ${
          darkMode
            ? 'bg-gradient-to-r from-orange-600/40 to-amber-600/40 border-orange-400/50 hover:from-orange-600/50 hover:to-amber-600/50'
            : 'bg-gradient-to-r from-orange-500/40 to-amber-500/40 border-orange-300/50 hover:from-orange-500/50 hover:to-amber-500/50'
        }`}
        aria-label="Open Hali Sales Agent"
      >
        <div className="flex items-center gap-2 px-2">
          <Sparkles className={`w-5 h-5 ${darkMode ? 'text-white' : 'text-orange-600'}`} />
          <span className="font-bold text-sm whitespace-nowrap">
            <span className={darkMode ? 'text-white' : 'text-gray-800'}>Hali</span>
            <span className={darkMode ? 'text-orange-400' : 'text-orange-500'}>Sales</span>
          </span>
        </div>
        {!isAIOnline && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
        )}
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${embedded ? 'relative w-full h-full min-h-0 max-w-full z-0' : `fixed ${customPosition ? '' : getPositionClasses()} z-50`} ${embedded ? 'w-full' : (compact ? 'w-80' : 'w-96')} ${embedded ? '' : 'max-w-[calc(100vw-48px)]'} ${isMinimized ? 'h-14' : ''} bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden border-2 transition-all duration-300 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
      style={{
        borderColor: branding?.primaryColor || undefined,
        height: embedded ? '100%' : (isMinimized ? undefined : 'min(640px, calc(100vh - 200px))'),
        maxHeight: embedded ? '100%' : (isMinimized ? undefined : 'calc(100vh - 120px)'),
        minHeight: embedded ? 0 : (isMinimized ? undefined : '360px'),
        resize: embedded || isMinimized ? 'none' : 'both',
        right: embedded ? undefined : (customPosition ? 'auto' : undefined),
        bottom: embedded ? undefined : (customPosition ? 'auto' : undefined),
        left: embedded ? undefined : (customPosition ? `${customPosition.x}px` : undefined),
        top: embedded ? undefined : (customPosition ? `${customPosition.y}px` : undefined)
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 border-b dark:border-gray-700 select-none"
        style={{
          backgroundColor: branding?.primaryColor ? `${branding.primaryColor}15` : undefined,
          borderColor: branding?.primaryColor || undefined,
          cursor: embedded ? 'default' : (dragState.isDragging ? 'grabbing' : 'grab')
        }}
        onMouseDown={(event) => {
          if (embedded) return;
          if (event.button !== 0) return;
          if (event.target.closest('button')) return;
          if (!containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          setCustomPosition({ x: rect.left, y: rect.top });
          setDragState({
            isDragging: true,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top
          });
        }}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="p-2 rounded-lg"
            style={{
              backgroundColor: branding?.primaryColor || '#F97316'
            }}
          >
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm truncate">
              <span className="dark:text-white text-gray-800">Hali</span>
              <span className="text-orange-500">Sales</span>
              <span className="dark:text-gray-300 text-gray-600 font-normal"> Agent</span>
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-xs ${isAIOnline ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {isAIOnline ? '● Online' : '● Offline'}
              </span>
              {qualificationStatus && !isLiveMode && (
                <span className={`text-xs px-2 py-0.5 rounded-full border ${getQualificationBadgeColor()}`}>
                  {getQualificationLabel()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleResetConversation}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Reset discussion"
            title="Reset discussion"
          >
            <Trash2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </button>
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('supportChat:openLiveChat'));
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-colors bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-800/40"
            aria-label="Discuss with team"
          >
            <Users className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Talk to team</span>
          </button>
          {!embedded && (
            <>
              <button
                onClick={toggleMinimize}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label={isMinimized ? 'Expand' : 'Minimize'}
              >
                <Minimize2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <button
                onClick={toggleChat}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages Area */}
      {!isMinimized && (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${message.type === 'user'
                    ? 'bg-blue-500 text-white'
                    : message.isError
                      ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
                    }`}
                  style={
                    message.type === 'user' && branding?.primaryColor
                      ? { backgroundColor: branding.primaryColor }
                      : undefined
                  }
                >
                  {message.type === 'bot' && (
                    <div className="flex items-center gap-2 mb-1">
                      <Bot className="w-4 h-4" />
                      <span className="text-xs font-medium opacity-75">
                        {branding?.companyName || 'Hali Leads Agent'}
                      </span>
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {message.type === 'bot' ? renderBotMessageContent(message.text) : message.text}
                  </p>
                  {message.type === 'bot' && isConfirmationPrompt(message.text) && (
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleQuickReply('Yes')}
                        className="px-3 py-1.5 text-xs rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 hover:bg-emerald-200 dark:hover:bg-emerald-800/40 transition-colors"
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => handleQuickReply('No')}
                        className="px-3 py-1.5 text-xs rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-700 hover:bg-rose-200 dark:hover:bg-rose-800/40 transition-colors"
                      >
                        No
                      </button>
                    </div>
                  )}
                  {message.type === 'bot' && isContactPrompt(message.text) && (
                    <div className="mt-3 grid gap-2 text-xs">
                      <input
                        type="text"
                        placeholder="Full name"
                        value={contactDraft.fullName}
                        onChange={(event) => setContactDraft((prev) => ({ ...prev, fullName: event.target.value }))}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-gray-100"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={contactDraft.email}
                        onChange={(event) => setContactDraft((prev) => ({ ...prev, email: event.target.value }))}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-gray-100"
                      />
                      <input
                        type="text"
                        placeholder="Company name"
                        value={contactDraft.company}
                        onChange={(event) => setContactDraft((prev) => ({ ...prev, company: event.target.value }))}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-gray-100"
                      />
                      <input
                        type="tel"
                        placeholder="Mobile phone number"
                        value={contactDraft.phone}
                        onChange={(event) => setContactDraft((prev) => ({ ...prev, phone: event.target.value }))}
                        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-gray-900 dark:text-gray-100"
                      />
                      <button
                        type="button"
                        onClick={handleContactSubmit}
                        disabled={contactSubmitting}
                        className="mt-1 inline-flex items-center justify-center rounded bg-blue-600 text-white px-3 py-1.5 text-xs hover:bg-blue-700 disabled:opacity-60"
                      >
                        {contactSubmitting ? 'Saving…' : 'Save details'}
                      </button>
                    </div>
                  )}
                  <span className="text-xs opacity-60 mt-1 block">
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Loader className="w-4 h-4 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">Typing...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t dark:border-gray-700 bg-white dark:bg-gray-800">
            {autoLogin?.redirectUrl && (
              <div className="mb-3 rounded-lg border border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-xs text-emerald-800 dark:text-emerald-200">
                <div className="font-semibold">Account created and signed in.</div>
              <div className="mt-1">Continue in your dashboard and keep this chat open.</div>
              <button
                type="button"
                onClick={() => { window.location.href = autoLogin.redirectUrl; }}
                className="mt-2 inline-flex items-center justify-center rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                Open Dashboard
              </button>
            </div>
          )}
            {facilityUi && (
              <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">
                  Facility Amount Requested
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Amount"
                    value={facilityDraft.amount}
                    onChange={(event) => {
                      const inputValue = event.target.value;
                      // Allow empty
                      if (inputValue === '') {
                        setFacilityDraft((prev) => ({ ...prev, amount: '' }));
                        return;
                      }
                      // Remove commas for validation, then format
                      const numericOnly = inputValue.replace(/,/g, '');
                      // Allow numbers and one decimal point
                      if (/^\d*\.?\d*$/.test(numericOnly)) {
                        const formatted = formatNumberWithSeparator(numericOnly);
                        setFacilityDraft((prev) => ({ ...prev, amount: formatted }));
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleFacilitySubmit();
                      }
                    }}
                    className="flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                  <select
                    value={facilityDraft.currency}
                    onChange={(event) => setFacilityDraft((prev) => ({ ...prev, currency: event.target.value }))}
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm text-gray-900 dark:text-gray-100"
                  >
                    {facilityDraft.currencies.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleFacilitySubmit}
                    disabled={!facilityDraft.amount || isTyping}
                    className="rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: branding?.primaryColor || '#3B82F6' }}
                  >
                    Send
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={facilityUi ? 'Use the facility amount field above…' : 'Ask about invoice factoring...'}
                className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                rows={1}
                style={{
                  focusRingColor: branding?.primaryColor || undefined
                }}
                disabled={isTyping || Boolean(facilityUi)}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isTyping || Boolean(facilityUi)}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: branding?.primaryColor || '#3B82F6',
                  color: '#FFFFFF'
                }}
                aria-label="Send message"
              >
                {isTyping ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
              <input
                type="file"
                id="hali-leads-upload"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.png,.jpg,.jpeg"
                className="hidden"
                disabled={isUploading}
              />
              <label
                htmlFor="hali-leads-upload"
                className={`p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                title="Upload Document"
                tabIndex={isUploading ? -1 : 0}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && !isUploading) {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                {isUploading ? <Loader className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </label>
            </div>
            {!isAIOnline && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                Connection lost. Please check your internet connection.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HaliLeadsAgent;
